import { useEffect, useMemo, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, AlertCircle, Save } from 'lucide-react';
import { SchemaBuilder } from '@/components/activations/SchemaBuilder';
import { ActivationConfigForm } from '@/components/activations/ActivationConfigForm';
import {
  useActivationTemplate,
  useCreateActivationTemplate,
  useUpdateActivationTemplate,
} from '@/hooks/useActivationTemplates';
import {
  ACTIVATION_TYPES,
  ACTIVATION_TYPE_LABELS,
  ACTIVATION_TYPE_FAMILY,
  buildTypedConfig,
  readConfigValues,
  type ActivationType,
  type CustomizationSchema,
  type TypedActivationConfig,
} from '@/types/activations';
import type { CreateActivationTemplateRequest } from '@/types/generated/activations.generated';

const EMPTY_SCHEMA: CustomizationSchema = { version: 1, fields: [] };

export default function ActivationTemplateEditorPage() {
  const [, navigate] = useLocation();
  const [, params] = useRoute('/activations/templates/:templateId');
  const templateId = params?.templateId && params.templateId !== 'new' ? params.templateId : undefined;
  const isEdit = !!templateId;

  const { data: existing, isLoading, isError, error } = useActivationTemplate(templateId);
  const createMutation = useCreateActivationTemplate();
  const updateMutation = useUpdateActivationTemplate();

  // Form state
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [activationType, setActivationType] = useState<ActivationType>('RaffleDraw');
  const [isGlobal, setIsGlobal] = useState(true);
  const [schema, setSchema] = useState<CustomizationSchema>(EMPTY_SCHEMA);
  // DefaultConfig is authored with the SAME canonical typed form used at attach
  // (camelCase keys: outcome, prizeName, model, prompts…), so a template's
  // defaults actually prefill the per-type ActivationConfigForm on attach.
  const [defaultConfig, setDefaultConfig] = useState<TypedActivationConfig>(() =>
    buildTypedConfig('RaffleDraw'),
  );
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setName(existing.name ?? '');
      setNameAr(existing.nameAr ?? '');
      setActivationType(existing.activationType);
      setIsGlobal(existing.isGlobal ?? true);
      setSchema((existing.customizationSchema as CustomizationSchema | null) ?? EMPTY_SCHEMA);
      setDefaultConfig(
        buildTypedConfig(existing.activationType, readConfigValues(existing.defaultConfig)),
      );
    }
  }, [existing]);

  // When the type changes on a NEW template, reseed the typed defaults to that type.
  useEffect(() => {
    if (!isEdit) setDefaultConfig((prev) => buildTypedConfig(activationType, prev));
  }, [activationType, isEdit]);

  const family = useMemo(() => ACTIVATION_TYPE_FAMILY[activationType], [activationType]);

  const validate = (): boolean => {
    if (name.trim().length < 2) {
      setNameError('Name must be at least 2 characters.');
      return false;
    }
    setNameError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const cleanSchema = schema.fields.length > 0 ? schema : null;
    // Persist the canonical typed default config (flat camelCase keys) so attach
    // can read it straight back via readConfigValues + buildTypedConfig.
    const cleanConfig = defaultConfig as unknown as Record<string, unknown>;

    if (isEdit) {
      await updateMutation.mutateAsync({
        id: templateId!,
        data: {
          name: name.trim(),
          nameAr: nameAr.trim() || null,
          defaultConfig: cleanConfig,
          customizationSchema: cleanSchema,
        },
      });
    } else {
      await createMutation.mutateAsync({
        // The generated request type still has the narrow ActivationType union
        // (BlazePod not yet in swagger). BlazePod is a real backend value, so the
        // cast is safe; drop it once /swagger-gen is re-run. See types/activations.ts.
        activationType: activationType as CreateActivationTemplateRequest['activationType'],
        activationFamily: family,
        name: name.trim(),
        nameAr: nameAr.trim() || null,
        isGlobal,
        defaultConfig: cleanConfig,
        customizationSchema: cleanSchema,
      });
    }
    navigate('/activations/templates');
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  if (isEdit && isLoading) {
    return (
      <div className="flex-1 p-6" data-testid="template-editor-loading">
        <Skeleton className="mb-4 h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isEdit && isError) {
    return (
      <div className="flex-1 p-6">
        <Alert variant="destructive" data-testid="template-editor-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load template</AlertTitle>
          <AlertDescription>{(error as Error)?.message || 'Something went wrong.'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex-1" data-testid="template-editor-page">
      <Header title={isEdit ? 'Edit Template' : 'New Template'} description="Define the activation recipe and its bounded customization surface.">
        <Button variant="outline" onClick={() => navigate('/activations/templates')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={handleSave} disabled={saving} data-testid="save-template-button">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving…' : 'Save template'}
        </Button>
      </Header>

      <div className="space-y-6 p-6">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tpl-name">Name *</Label>
                <Input
                  id="tpl-name"
                  data-testid="template-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {nameError && <p className="text-xs text-destructive" data-testid="template-name-error">{nameError}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tpl-name-ar">Name (Arabic)</Label>
                <Input
                  id="tpl-name-ar"
                  data-testid="template-name-ar"
                  dir="rtl"
                  value={nameAr}
                  onChange={(e) => setNameAr(e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tpl-type">Activation type</Label>
                <Select
                  value={activationType}
                  disabled={isEdit}
                  onValueChange={(v) => setActivationType(v as ActivationType)}
                >
                  <SelectTrigger id="tpl-type" data-testid="template-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVATION_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {ACTIVATION_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Family: {family}{isEdit && ' (type is immutable after creation)'}
                </p>
              </div>
              <div className="flex items-end">
                <div className="flex items-center gap-3">
                  <Switch
                    id="tpl-global"
                    data-testid="template-is-global"
                    checked={isGlobal}
                    disabled={isEdit}
                    onCheckedChange={setIsGlobal}
                  />
                  <Label htmlFor="tpl-global">
                    Global template (selectable by any org)
                    {isEdit && <span className="block text-xs text-muted-foreground">Tenancy is immutable after creation.</span>}
                  </Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Customization schema &amp; defaults</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="schema">
              <TabsList>
                <TabsTrigger value="schema" data-testid="tab-schema">Customization schema</TabsTrigger>
                <TabsTrigger value="defaults" data-testid="tab-defaults">Default config (preview)</TabsTrigger>
              </TabsList>
              <TabsContent value="schema" className="pt-4">
                <p className="mb-4 text-sm text-muted-foreground">
                  Declare the bounded set of fields that can be customised per event/activation. The
                  same schema drives the customization form in the CMS and the runner in the
                  activations portal.
                </p>
                <SchemaBuilder schema={schema} onChange={setSchema} />
              </TabsContent>
              <TabsContent value="defaults" className="pt-4">
                <p className="mb-4 text-sm text-muted-foreground">
                  Default values applied when an activation does not override the required config.
                  This is the same typed form used at attach (canonical keys), so these defaults
                  prefill the attach dialog directly. Input-asset selection is per-event only.
                </p>
                <ActivationConfigForm
                  type={activationType}
                  value={defaultConfig}
                  onChange={setDefaultConfig}
                  eventId=""
                  idPrefix="default"
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
